import { useEffect, useState } from "react";

import { General } from "../util/General";
import useValue from "./useValue";
import * as webUserManagementService from "../services/webUserManagementService";
import { CustomNotification } from "../util/CustomNotification";

import type { GetAllUserResponse } from "../shared/interface/UserManagement/user.interface";
import useTablePagination from "./useTablePagination";
import { TableHelper } from "../util/TableHelper";
import { useSelector } from "react-redux";
import { RootState } from "../appRedux/store";

const useGetWebUserList = () => {
  /* STATE */
  const [userList, setUserList] = useState<GetAllUserResponse[]>([]);
  const [reFetchOrderList, setRefetchOrderList] = useState(General.getGUID());
  const [filteredTableData, setFilteredTableData] = useState<GetAllUserResponse[]>([]);

  const { value: loading, handleDisableValue: handleDisableLoading, handleEnableValue: handleEnableLoading } = useValue(true);

  /* CUSTOM HOOKS */
  const { pagination, handleChangePagination, handleChangeTotalSize } = useTablePagination(0);

  const { masterFilters } = useSelector(({ common }: RootState) => common);

  /* HANDLERS */
  const handleRefetchUserList = () => setRefetchOrderList(General.getGUID());

  const handleFilterTableData = (searchedText: string | undefined) => {
    const keys = ["customer_name", "username", "full_name", "email_address", "mobile_number", "role_name"];
    const result = TableHelper.getFilterDataSource(userList, keys, searchedText) as GetAllUserResponse[];

    /* update filter state */
    setFilteredTableData(result);

    /* update pagination */
    handleChangeTotalSize(result?.length);
  };

  /* EFFECTS */
  useEffect(() => {
    const fetchUserList = async () => {
      handleEnableLoading();

      const showDeleted = true;

      const data = await webUserManagementService.getAllUserList(showDeleted, masterFilters?.customerId, masterFilters?.locationId);

      /* add key to each item in array */
      const formattedData = data?.["users"]?.map((item, index) => {
        /* Get the list of customer names from userCustomerLocations */
        const customerNames = item?.userCustomerLocations?.map((location) => location?.customer?.customer_name || "")?.filter((name) => name !== "");

        /* Remove duplicate customer names using reduce */
        const uniqueCustomerNames = customerNames.reduce<string[]>((acc, name) => {
          if (!acc.includes(name)) {
            acc.push(name);
          }
          return acc;
        }, []);

        /* Join customer names into a comma-separated string */
        const commaSeparatedCustomerNames = uniqueCustomerNames.join(", ");

        return {
          ...item,
          sr_no: index + 1,
          customer_name: commaSeparatedCustomerNames,
          full_name: `${item?.first_name ?? ""} ${item?.last_name ?? ""}`,
          role_name: item?.role?.name
        };
      });

      return formattedData;
    };

    fetchUserList()
      .then((data) => {
        setUserList(data);
        setFilteredTableData(data);
        handleChangeTotalSize(data?.length);
      })
      .catch((error) => {
        CustomNotification.showErrorMessage(error);
      })
      .finally(() => {
        handleDisableLoading();
      });

    // eslint-disable-next-line
  }, [reFetchOrderList, masterFilters?.customerId, masterFilters?.locationId]);

  return { loading, userList, filteredTableData, handleRefetchUserList, pagination, handleChangePagination, handleChangeTotalSize, handleFilterTableData };
};

export default useGetWebUserList;
